<template>
  <div id="app" :class="{ 'my-app-dark': isDarkMode }">
    <router-view/>
  </div>
</template>

<script>
import { onMounted, computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'App',
  setup() {
    const store = useStore();
    const isDarkMode = computed(() => store.state.isDarkMode);
    
    onMounted(async () => {
      await store.dispatch('fetchSession');
      
      // Check localStorage for dark mode preference
      const savedDarkMode = localStorage.getItem('darkMode');
      
      // If dark mode preference exists in localStorage
      if (savedDarkMode === 'true') {
        document.body.classList.add('dark-mode');
        document.documentElement.classList.add('my-app-dark');
      } else if (savedDarkMode === 'false') {
        document.body.classList.remove('dark-mode');
        document.documentElement.classList.remove('my-app-dark');
      } else {
        // If no preference is saved, check system preference
        const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDarkMode) {
          document.body.classList.add('dark-mode');
          document.documentElement.classList.add('my-app-dark');
          localStorage.setItem('darkMode', 'true');
        } else {
          localStorage.setItem('darkMode', 'false');
        }
      }
      
      // Listen for changes in dark mode preference
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (e.matches) {
          document.body.classList.add('dark-mode');
          document.documentElement.classList.add('my-app-dark');
          localStorage.setItem('darkMode', 'true');
        } else {
          document.body.classList.remove('dark-mode');
          document.documentElement.classList.remove('my-app-dark');
          localStorage.setItem('darkMode', 'false');
        }
      });
    });

    return {
      isDarkMode
    };
  }
};
</script>

<style lang="scss">
:root {
  --primary-color-rgb: 59, 130, 246;
  --primary-color: #3b82f6;
  --primary-color-lighter: #90c2ff;
  --primary-color-darker: #3b75db;
  
  /* Light Theme Variables */
  --surface-ground: #f8fafc;
  --surface-section: #f1f5f9;
  --surface-card: #ffffff;
  --surface-overlay: #ffffff;
  --surface-border: #e2e8f0;
  --surface-hover: #f5f8fc;
  --text-color: #334155;
  --text-color-secondary: #64748b;
  
  /* Common Variables */
  --primary-50: #eff6ff;
  --primary-100: #dbeafe;
  --primary-200: #bfdbfe;
  --primary-300: #93c5fd;
  --primary-400: #60a5fa;
  --primary-500: #3b82f6;
  --primary-600: #2563eb;
  --primary-700: #1d4ed8;
  --primary-800: #1e40af;
  --primary-900: #1e3a8a;
  --primary-950: #172554;
}

/* Dark Mode Variables */
.dark-mode {
  --surface-ground: #0f172a;
  --surface-section: #1e293b;
  --surface-card: #1e293b;
  --surface-overlay: #1e293b;
  --surface-border: #334155;
  --surface-hover: #334155;
  --text-color: #f8fafc;
  --text-color-secondary: #cbd5e1;
}

/* PrimeVue Dark Theme Variables */
html.my-app-dark {
  --surface-a: #1e293b;
  --surface-b: #0f172a;
  --surface-c: #1e293b;
  --surface-d: #334155;
  --surface-e: #1e293b;
  --surface-f: #1e293b;
  --text-color: #f8fafc;
  --text-color-secondary: #cbd5e1;
  --primary-color: #3b82f6;
  --primary-color-lighter: #60a5fa;
  --surface-border: #334155;
  --surface-border-light: #334155;
  --surface-hover: #334155;
}

html, body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
}

body {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, Avenir, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--surface-ground);
  color: var(--text-color);
  line-height: 1.5;
}

#app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

a {
  text-decoration: none;
  color: var(--primary-color);
  transition: color 0.2s ease, transform 0.2s ease;
  
  &:hover {
    color: var(--primary-color-darker);
    transform: translateY(-1px);
  }
}

/* Global PrimeVue Component Overrides */
.p-component {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, Avenir, Arial, sans-serif;
}

.p-button {
  border-radius: 8px;
  
  &.p-button-text {
    color: var(--primary-color);
    
    &:hover {
      background-color: var(--surface-hover);
      color: var(--primary-color-darker);
    }
  }
  
  &.p-button-outlined {
    border-color: var(--primary-color);
    color: var(--primary-color);
    
    &:hover {
      background-color: rgba(var(--primary-color-rgb), 0.04);
      border-color: var(--primary-color-darker);
      color: var(--primary-color-darker);
    }
  }
  
  &.p-button-primary {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color-darker);
      border-color: var(--primary-color-darker);
    }
  }
}

.p-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  
  .p-dialog-header {
    background-color: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
  }
  
  .p-dialog-content {
    background-color: var(--surface-card);
  }
  
  .p-dialog-footer {
    background-color: var(--surface-card);
  }
}

.p-card {
  border-radius: 12px;
  background-color: var(--surface-card);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
}

.p-toast {
  .p-toast-message {
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    
    &.p-toast-message-info {
      background-color: var(--primary-50);
      border-left-color: var(--primary-color);
      color: var(--primary-900);
      
      .p-toast-message-icon {
        color: var(--primary-color);
      }
    }
  }
}

/* Form elements */
.p-inputtext {
  border: 1px solid var(--surface-border);
  background-color: var(--surface-card);
  color: var(--text-color);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  
  &:enabled:hover {
    border-color: var(--primary-color);
  }
  
  &:enabled:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
  }
}

.p-dropdown {
  border: 1px solid var(--surface-border);
  background-color: var(--surface-card);
  border-radius: 8px;
  
  &:not(.p-disabled).p-focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
  }
}

/* Utility classes */
.flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-column {
  flex-direction: column;
}

.align-items-center {
  align-items: center;
}

.align-items-start {
  align-items: flex-start;
}

.align-items-end {
  align-items: flex-end;
}

.justify-content-center {
  justify-content: center;
}

.justify-content-between {
  justify-content: space-between;
}

.justify-content-end {
  justify-content: flex-end;
}

.justify-content-start {
  justify-content: flex-start;
}

.flex-wrap {
  flex-wrap: wrap;
}

.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

.text-right {
  text-align: right;
}

.w-full {
  width: 100%;
}

.h-full {
  height: 100%;
}

/* Spacing */
.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}

.gap-4 {
  gap: 1rem;
}

.m-0 {
  margin: 0;
}

.m-auto {
  margin: auto;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-3 {
  margin-right: 0.75rem;
}

.mr-4 {
  margin-right: 1rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.ml-3 {
  margin-left: 0.75rem;
}

.ml-4 {
  margin-left: 1rem;
}

.ml-auto {
  margin-left: auto;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.p-1 {
  padding: 0.25rem;
}

.p-2 {
  padding: 0.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.p-4 {
  padding: 1rem;
}

/* Typography */
.font-medium {
  font-weight: 500;
}

.font-semibold {
  font-weight: 600;
}

.font-bold {
  font-weight: 700;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-base {
  font-size: 1rem;
  line-height: 1.5rem;
}

.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}

.text-5xl {
  font-size: 3rem;
  line-height: 1;
}

.text-6xl {
  font-size: 3.75rem;
  line-height: 1;
}

.text-color-secondary {
  color: var(--text-color-secondary);
}

.text-500 {
  color: var(--text-color-secondary);
}

.text-primary {
  color: var(--primary-color);
}

/* Display */
.block {
  display: block;
}

.inline-block {
  display: inline-block;
}

.inline {
  display: inline;
}

.hidden {
  display: none;
}

.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.sticky {
  position: sticky;
}

.fixed {
  position: fixed;
}

/* PrimeVue Overrides ve Input Stilleri */
.p-inputtext {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-inputtext:enabled:hover {
  border-color: var(--primary-color) !important;
}

.p-inputtext:enabled:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2) !important;
}

.p-password .p-password-input {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-dropdown {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-dropdown:not(.p-disabled).p-focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2) !important;
}

.p-checkbox .p-checkbox-box {
  border: 2px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-checkbox .p-checkbox-box.p-highlight {
  border-color: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
}

.p-radiobutton .p-radiobutton-box {
  border: 2px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-card {
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-button.p-button-outlined {
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.p-button.p-button-text {
  color: var(--primary-color) !important;
}

.p-button.p-button-secondary.p-button-outlined {
  color: var(--text-color-secondary) !important;
  border-color: var(--surface-border) !important;
}

/* PrimeVue Overrides */
.p-component {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, Avenir, Arial, sans-serif;
}

.p-inputtext {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-inputtext:enabled:hover {
  border-color: var(--primary-color) !important;
}

.p-inputtext:enabled:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2) !important;
}

.p-password .p-password-input {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-dropdown {
  border: 1px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-dropdown:not(.p-disabled).p-focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2) !important;
}

.p-checkbox .p-checkbox-box {
  border: 2px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-checkbox .p-checkbox-box.p-highlight {
  border-color: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
}

.p-radiobutton .p-radiobutton-box {
  border: 2px solid var(--surface-border-light, var(--surface-border)) !important;
  background-color: var(--surface-a, white) !important;
}

.p-card {
  background-color: var(--surface-a, white) !important;
  color: var(--text-color) !important;
}

.p-button.p-button-outlined {
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.p-button.p-button-text {
  color: var(--primary-color) !important;
}

.p-button.p-button-secondary.p-button-outlined {
  color: var(--text-color-secondary) !important;
  border-color: var(--surface-border) !important;
}

</style>
